import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { ArrowRight, GraduationCap } from "lucide-react";

const products = [
  {
    name: "Vidyora",
    tagline: "The Future of Education Management",
    description: "A comprehensive school management system designed to streamline administration, enhance communication, and empower educators with powerful digital tools. Vidyora is our flagship product, built from the ground up to address the unique challenges faced by educational institutions.",
    link: "/vidyora",
    icon: GraduationCap,
    accent: "orange",
    image: "https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Products() {
  return (
    <div data-testid="products-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono" data-testid="products-badge">
              OUR PRODUCTS
            </Badge>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-[1.1] tracking-tight">
              Products we've{" "}
              <span className="gradient-text">built.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl">
              Beyond client projects, we invest in building our own products 
              that solve real-world problems with innovative technology.
            </p>
          </div>
        </div>
      </section>

      {/* Products List */}
      <section className="relative py-24 lg:py-32" data-testid="products-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {products.map((product, i) => (
            <div
              key={i}
              data-testid={`product-card-${i}`}
              className="grid lg:grid-cols-2 gap-12 items-center p-8 lg:p-12 rounded-2xl bg-slate-900/50 border border-orange-500/20 hover:border-orange-500/30 transition-all"
            >
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
                    <product.icon className="w-6 h-6 text-orange-400" />
                  </div>
                  <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 hover:bg-orange-500/10 text-xs font-mono">
                    FLAGSHIP PRODUCT
                  </Badge>
                </div>
                <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight mb-2">
                  <span className="gradient-text-orange">{product.name}</span>
                </h2>
                <p className="text-orange-300/80 text-sm font-medium mb-4">{product.tagline}</p>
                <p className="text-slate-400 text-sm leading-relaxed mb-8">{product.description}</p>
                <Link
                  to={product.link}
                  data-testid="product-vidyora-link"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] hover:-translate-y-0.5"
                >
                  Explore Vidyora
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden border border-orange-500/10 shadow-[0_0_40px_rgba(249,115,22,0.08)]">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-72 object-cover"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Future Products Teaser */}
      <section className="relative py-24 lg:py-32 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
            More products <span className="gradient-text">coming soon.</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-lg mx-auto">
            We're constantly innovating. Stay tuned for more products that 
            solve real-world challenges with elegant technology.
          </p>
          <Link
            to="/contact"
            data-testid="products-cta"
            className="mt-8 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5"
          >
            Partner with Us
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
